// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/prepaidLoyalty/paymentMethodTabStyle';
import { translate } from 'components/Translation';
import { HeadingSBold, Caption } from 'components/Typography/Typography';
import CustomRadio from 'components/CustomRadio/CustomRadio';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CardInfo from './subcomponents/CardInfo';
import MasterNumberForm from './subcomponents/MasterNumberForm';
import { PAYMENT_METHODS } from 'views/PrepaidLoyalty/commons/constants';
import { CorpUserData } from 'utils/api/models/users/corpUserData';
import { config } from 'views/PrepaidLoyalty/config';
import AttentionCCLimitNote from '../../attentionCCLimitNote';
import SaveCardToggle from './subcomponents/SaveCardToggle';
import { IPaymentToken } from 'utils/api/models/payments/paymentTokens';
import PaymentService from 'services/payments/payment';
import Cards from './subcomponents/Cards';
import { BillsService } from 'services/bills/bills';
import { SingleBillId } from 'utils/api/models/bills/singleBillId';
import { finalize } from 'rxjs/operators';
import { SingleBillingNumber } from 'utils/api/models/accounts/singleBillingNumber';
import CustomButton from 'components/CustomButtons/Button';
import { useDispatch } from 'react-redux';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { DepartmentsService } from 'services/departments/departments';
import { AccountsService } from 'services/accounts/accounts';
import { upIdSelector, userNameSelector, emailSelector, contactNumberSelector } from 'redux/modules/session/selectors';
import { useSelector } from 'react-redux';
import { SingleAccountManager } from 'utils/api/models/users/singleAccountManager';
import { ContactService } from 'services/contact/contact';
import { getLanguage } from 'utils/language/language';

// @ts-ignore
const useStyles = makeStyles(styles);

const ENTERPRISE_LOYALTY = 'ENTERPRISE_LOYALTY';
const OFFER_DIRECT_MESSAGE_NAME = 'prepaid_loyalty_manager';

type IPaymentMethodTabProps = {
	paymentMethod: number;
	creditCardData: types.topUpAddOns.IFormData;
	onPaymentMethodChange: (paymentMethod: number) => void;
	masterNumber: string;
	creditLimit?: string;
	balance?: string;
	setCreditLimit?: React.Dispatch<React.SetStateAction<string>>;
	setMasterNumber: (masterNumber: string) => void;
	setAccountNumber?: (accountNumber: string) => void;
	setAccountDetails?: (accountNumber: string) => void;
	setUserData?: (data: CorpUserData) => void;
	onCreditCardDataChange: types.topUpAddOns.setFormData;
	validation: types.topUpAddOns.IValidation;
	setValidation: React.Dispatch<React.SetStateAction<types.topUpAddOns.IValidation>>;
	paymentLimitReached: types.topUpAddOns.paymentLimitReached;
	checkValidity: boolean;
	numbersFromMasterSearch: SingleBillingNumber[];
	setNumbersFromMasterSearch: React.Dispatch<React.SetStateAction<SingleBillingNumber[]>>;
	saveCreditCard: boolean;
	toggleSaveCreditCard: () => void;
	selectedNumbersExceededLimit?: boolean;
};

const PaymentMethodTab = ({
	paymentMethod,
	creditCardData,
	onPaymentMethodChange,
	masterNumber,
	balance,
	creditLimit,
	setCreditLimit,
	setMasterNumber,
	setAccountNumber,
	setAccountDetails,
	setUserData,
	onCreditCardDataChange,
	validation,
	setValidation,
	paymentLimitReached,
	checkValidity,
	numbersFromMasterSearch,
	setNumbersFromMasterSearch,
	selectedNumbersExceededLimit = false,
	toggleSaveCreditCard,
	saveCreditCard,
}: IPaymentMethodTabProps) => {
	const classes = useStyles();
	const [showCvvInfo, setShowCvvInfo] = useState<boolean>(false);
	const [tokens, setTokens] = useState<IPaymentToken[]>([]);
	const [billingIds, setBillingIds] = useState<SingleBillId[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [disabledEmailSent, setDisabledEmailSent] = useState<boolean>(false);
	const [managers, setManagers] = useState<SingleAccountManager[]>([]);
	const [managersLoaded, setManagersLoaded] = useState<boolean>(false);
	const [documentId, setDocumentId] = useState<string>(null);

	const upId: number = useSelector(upIdSelector);
	const username: string = useSelector(userNameSelector);
	const userEmail: string = useSelector(emailSelector);
	const userPhone: string = useSelector(contactNumberSelector);
	const lang = getLanguage();
	const dispatch = useDispatch();

	useEffect(() => {
		setLoading(true);
		const subscription = BillsService.getCorpBillingIds()
			.pipe(finalize(() => setLoading(false)))
			.subscribe(({ billingIds }) => {
				setBillingIds(billingIds);
			});
		return () => {
			subscription.unsubscribe();
		};
	}, []);

	const filteredBillingIds = useMemo(() => {
		return billingIds.filter(({ addBillingType }) => addBillingType === ENTERPRISE_LOYALTY);
	}, [billingIds]);

	useEffect(() => {
		if (!loading) {
			if (
				billingIds.length > 0 &&
				filteredBillingIds.length > 0 &&
				paymentMethod != PAYMENT_METHODS.BY_CREDIT_CARD
			) {
				onPaymentMethodChange(PAYMENT_METHODS.BY_MASTER_NUMBER);
			} else {
				onPaymentMethodChange(PAYMENT_METHODS.BY_CREDIT_CARD);
			}
		}
	}, [billingIds, filteredBillingIds, onPaymentMethodChange, loading]);

	const errors = useMemo(() => {
		const isInvalid = (field: keyof types.topUpAddOns.IValidation) =>
			!validation[field].isPotentiallyValid || (checkValidity && !validation[field].isValid);
		return {
			number: isInvalid('number'),
			cvv: isInvalid('cvv'),
			date: isInvalid('date'),
		};
	}, [validation, checkValidity]);

	useEffect(() => {
		if (selectedNumbersExceededLimit && onPaymentMethodChange) {
			onPaymentMethodChange(PAYMENT_METHODS.BY_MASTER_NUMBER);
		}
	}, [selectedNumbersExceededLimit, onPaymentMethodChange]);

	useEffect(() => {
		const request = PaymentService.getPaymentTokens().subscribe(({ tokens }) => {
			setTokens(tokens);
			if (tokens.length > 0 && !creditCardData.number) {
				const token = tokens[0];
				onCreditCardDataChange('tokenId', token?.id);
				onCreditCardDataChange('type', 'token');
				onCreditCardDataChange('number', token?.cardNumber);
				onCreditCardDataChange('cardType', token?.cardType);
			}
		});
		dispatch(showLoadingIndicator());
		AccountsService.getCorpAccountDetails(upId).subscribe((account: AccountDetails) => {
			if (account) {
				const documentId = account?.accountDetails?.documentId;
				const documentType = account?.accountDetails?.documentType;
				setDocumentId(documentId);
				DepartmentsService.getAccountManagerDetails(documentId, documentType)
					.pipe(finalize(() => dispatch(hideLoadingIndicator())))
					.subscribe(
						({ managers }) => {
							setManagers(managers);
							setManagersLoaded(true);
						},
						() => {
							setManagersLoaded(true);
						}
					);
			} else {
				dispatch(hideLoadingIndicator());
			}
		});

		return () => {
			request.unsubscribe();
		};
	}, [onCreditCardDataChange, dispatch, upId]);

	useEffect(() => {
		onCreditCardDataChange('saveCard', saveCreditCard);
	}, [onCreditCardDataChange, saveCreditCard]);

	const sendEmail = () => {
		dispatch(showLoadingIndicator());
		setDisabledEmailSent(true);
		(managers || []).forEach((manager, i) => sendEmailToManager(manager?.emailAddress, managers?.length - 1 === i));
	};

	const sendEmailToManager = (emailAddress: string, message: boolean) => {
		ContactService.sendDirectMessage({
			offer: OFFER_DIRECT_MESSAGE_NAME,
			subject: translate(
				'prepaid.loyalty.add.new.services.payment.method.credit.card.account.managers.subject.message'
			),
			phone: userPhone,
			email: emailAddress,
			language: lang,
			message: JSON.stringify({
				username: username,
				documentId: documentId,
				email: userEmail,
			}),
			saveContact: true,
			returnReference: true,
			sendEmailToSender: true,
			sendCopyToSender: false,
			sendSms: false,
		})
			.pipe(
				finalize(() => {
					if (message) {
						dispatch(hideLoadingIndicator());
					}
				})
			)
			.subscribe(() => {});
	};

	return (
		<div className={classes.smallTab}>
			<div className={classes.tab}>
				<ConditionalRender
					show={loading}
					onFalse={() => (
						<>
							<div className={classes.paymentMethodTitleWrapper}>
								<HeadingSBold disableMargin>
									{translate('prepaid.loyalty.add.new.services.payment.method.title')}
								</HeadingSBold>
							</div>
							<div className={classes.paymentMethodTabContainer}>
								<CustomRadio
									checked={paymentMethod === PAYMENT_METHODS.BY_MASTER_NUMBER}
									label={translate('prepaid.loyalty.add.new.services.payment.method.master.number')}
									onChange={() => {
										onPaymentMethodChange(PAYMENT_METHODS.BY_MASTER_NUMBER);
									}}
									disabled={filteredBillingIds.length <= 0}
								/>
								<ConditionalRender
									show={filteredBillingIds.length <= 0 && managersLoaded}
									onTrue={() => (
										<div className={classes.monthlyBillBlocked}>
											<ConditionalRender
												show={!managers?.length}
												onTrue={() => (
													<Caption color="secondary-light">
														{translate(
															'prepaid.loyalty.add.new.services.payment.method.credit.card.account.managers.no'
														)}
													</Caption>
												)}
												onFalse={() => (
													<>
														<Caption color="secondary-light">
															{translate(
																'prepaid.loyalty.add.new.services.payment.method.credit.card.account.managers.yes'
															)}
														</Caption>
														<div>
															<CustomButton
																color="tertiary-light"
																disabled={disabledEmailSent}
																onClick={sendEmail}
															>
																{translate(
																	'prepaid.loyalty.add.new.services.payment.method.credit.card.account.managers.send'
																)}
															</CustomButton>
														</div>
													</>
												)}
											/>
										</div>
									)}
								/>
								<ConditionalRender
									show={
										paymentMethod === PAYMENT_METHODS.BY_MASTER_NUMBER &&
										filteredBillingIds.length > 0
									}
									onTrue={() => (
										<MasterNumberForm
											masterNumber={masterNumber}
											creditLimit={creditLimit}
											balance={balance}
											setCreditLimit={setCreditLimit}
											setMasterNumber={setMasterNumber}
											setAccountNumber={setAccountNumber}
											setAccountDetails={setAccountDetails}
											setUserData={setUserData}
											numbers={numbersFromMasterSearch}
											setNumbers={setNumbersFromMasterSearch}
											filteredBillingIds={filteredBillingIds}
										/>
									)}
								/>
								<ConditionalRender
									show={selectedNumbersExceededLimit}
									onTrue={() => (
										<AttentionCCLimitNote
											body={
												<span>
													{translate('prepaid.loyalty.pay.cc.attention.1')}{' '}
													<b>
														{translate(
															'prepaid.loyalty.pay.cc.attention.2',
															config.MAX_NUMBERS_CC_PAYMENT
														)}
													</b>
												</span>
											}
										/>
									)}
								/>
								<CustomRadio
									checked={paymentMethod === PAYMENT_METHODS.BY_CREDIT_CARD}
									disabled={selectedNumbersExceededLimit}
									label={translate('prepaid.loyalty.add.new.services.payment.method.credit.card')}
									onChange={() => {
										onPaymentMethodChange(PAYMENT_METHODS.BY_CREDIT_CARD);
									}}
								/>
								<ConditionalRender
									show={paymentMethod === PAYMENT_METHODS.BY_CREDIT_CARD}
									onTrue={() => (
										<ConditionalRender
											show={tokens.length > 0}
											onTrue={() => (
												<Cards
													tokens={tokens}
													formData={creditCardData}
													setFormData={onCreditCardDataChange}
													setTokenData={(token) =>
														onCreditCardDataChange('tokenId', token?.id)
													}
													setSaveCard={toggleSaveCreditCard}
													validation={validation}
													setValidation={setValidation}
												/>
											)}
											onFalse={() => (
												<div className={classes.cardInfoContainer}>
													<CardInfo
														errors={errors}
														validation={validation}
														showCvvInfo={showCvvInfo}
														setShowCvvInfo={setShowCvvInfo}
														formData={creditCardData}
														setFormData={onCreditCardDataChange}
														paymentLimitReached={paymentLimitReached}
														setValidation={setValidation}
													/>
													<SaveCardToggle
														checked={saveCreditCard}
														toggle={toggleSaveCreditCard}
													/>
												</div>
											)}
										/>
									)}
								/>
							</div>
						</>
					)}
				/>
			</div>
		</div>
	);
};

export default PaymentMethodTab;
